/**
 * Interface for filter API response
 */
export interface FilterResponse {
  summary: FilterSummary[];
  filters: FilterOption[];
}

/**
 * Interface for filter summary data
 */
export interface FilterSummary {
  stock_count: number;
  fields: {
    delivery_date: string;
    mvtItemIdName: string;
    cappCategoriesId: number;
    vendorId: number;
    vendorIdName: string;
    mvtItemId: number;
    cappCategoriesIdName: string;
  };
}

/**
 * Interface for filter options
 */
export interface FilterOption {
  filterDisplayName: string;
  sequence: number;
  values: FilterValue[];
  filterName: string;
  type: 'select' | 'date' | 'text' | 'number' | 'multiselect';
}

/**
 * Interface for individual filter values
 */
export interface FilterValue {
  name: string;
  id: number | string;
  stockCount: number;
}

/**
 * Interface for applied filters state
 */
export interface MaterialRequestFilters {
  // Basic filters
  search?: string;
  projectAddressId?: number[];
  mvtItemId?: number[];
  vendorId?: number[];
  statusCdId?: number[];
  cappCategoriesId?: number[];
  
  // Date filters
  deliveryDateFrom?: Date;
  deliveryDateTo?: Date;
  createdDateFrom?: Date;
  createdDateTo?: Date;
  
  // Advanced filters
  isMr?: boolean;
  mrReadYn?: string;
  hasPrice?: boolean;
  hasInstructions?: boolean;
  hasImages?: boolean;
  
  // Quantity and price ranges
  quantityMin?: number;
  quantityMax?: number;
  priceMin?: number;
  priceMax?: number;
  
  // Location filters
  city?: string[];
  state?: string[];
  pincode?: string[];
  
  // Vendor and customer filters
  customerName?: string;
  vendorRating?: number;
  assignedVendorsCount?: number;
  
  // Sorting and pagination
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

/**
 * Interface for filter form data
 */
export interface FilterFormData {
  search: string;
  projectAddressIds: number[];
  mvtItemIds: number[];
  vendorIds: number[];
  statusCdIds: number[];
  cappCategoriesIds: number[];
  deliveryDateRange: {
    start: Date | null;
    end: Date | null;
  };
  createdDateRange: {
    start: Date | null;
    end: Date | null;
  };
  showMrOnly: boolean;
  showUnreadOnly: boolean;
  showWithPriceOnly: boolean;
  showWithInstructionsOnly: boolean;
  showWithImagesOnly: boolean;
  quantityRange: {
    min: number | null;
    max: number | null;
  };
  priceRange: {
    min: number | null;
    max: number | null;
  };
  cities: string[];
  states: string[];
  pincodes: string[];
  vendorRatingMin: number | null;
  assignedVendorsMin: number | null;
}

/**
 * Interface for saved filter presets
 */
export interface FilterPreset {
  id: string;
  name: string;
  description?: string;
  filters: MaterialRequestFilters;
  isDefault?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Enum for predefined filter types
 */
export enum FilterPresetType {
  ALL = 'all',
  PENDING_QUOTES = 'pending_quotes',
  NEW_REQUESTS = 'new_requests',
  IN_NEGOTIATION = 'in_negotiation',
  URGENT_DELIVERY = 'urgent_delivery',
  HIGH_VALUE = 'high_value',
  UNREAD = 'unread',
  MY_PROJECTS = 'my_projects',
  THIS_WEEK = 'this_week',
  THIS_MONTH = 'this_month',
  // "Won" stat tile on the Material Requests page (see WON_STATUS_CODES)
  WON = 'won'
}

/**
 * Predefined filter configurations
 * Note: statusCodes values are status code strings (e.g., 'QUOT', 'EDIT', 'NEGO')
 * The component will map status codes to actual option IDs from the API
 * Available status codes from API: EDIT(4), QUOT(5), NEGO(6), VASS(7), TRST(8), ODCF(9), ACWC(11), ODDP(23), GNPO(27), CLSD(28), DLTD(29), APQV(30), RTQV(31)
 */
/**
 * Stable status-code -> statusCdId map (status master ids).
 * Used as a fallback when the live filters-v2 options don't contain a status
 * (e.g. options haven't loaded yet, or the dataset currently has no items in
 * that status), so preset chips can still send a statusCdId to the backend.
 * Source: see status code -> id documentation above.
 * Ids not listed here are resolved from the live filters-v2 options.
 */
export const STATUS_CODE_TO_ID: Record<string, number> = {
  NEW: 1,
  EDIT: 4,
  QUOT: 5,
  NEGO: 6,
  VASS: 7,
  TRST: 8,
  ODCF: 9,
  ACWC: 11,
  ODDP: 23,
  GNPO: 27,
  CLSD: 28,
  DLTD: 29,
  APQV: 30,
  RTQV: 31
};

/**
 * Statuses that mean the request was awarded to the vendor (quote selected,
 * PO generated, order confirmed / dispatched). Drives the "Won" preset, the
 * "Won" stat tile and the row banner, so all three agree.
 */
export const WON_STATUS_CODES: string[] = ['VASS', 'ACPT', 'ACWC', 'ACPO', 'APPR', 'APQV', 'GNPO', 'ODCF', 'ODDP'];

export const FILTER_PRESETS: Record<FilterPresetType, Partial<MaterialRequestFilters & { statusCodes?: string[] }>> = {
  [FilterPresetType.ALL]: {},

  [FilterPresetType.WON]: {
    statusCodes: WON_STATUS_CODES
  },
  
  [FilterPresetType.PENDING_QUOTES]: {
    statusCodes: ['QUOT'] // Maps to id: 5
  },
  
  [FilterPresetType.NEW_REQUESTS]: {
    statusCodes: ['EDIT'] // Maps to id: 4 - Using EDIT as closest to "new/edited" requests
  },
  
  [FilterPresetType.IN_NEGOTIATION]: {
    statusCodes: ['NEGO'] // Maps to id: 6
  },
  
  [FilterPresetType.URGENT_DELIVERY]: {
    deliveryDateFrom: new Date(),
    deliveryDateTo: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // Next 7 days
  },
  
  [FilterPresetType.HIGH_VALUE]: {
    priceMin: 100000 // Requests above 1 lakh
  },
  
  [FilterPresetType.UNREAD]: {
    mrReadYn: 'N'
  },
  
  [FilterPresetType.MY_PROJECTS]: {
    // Will be populated based on user's projects
  },
  
  [FilterPresetType.THIS_WEEK]: {
    createdDateFrom: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    createdDateTo: new Date()
  },
  
  [FilterPresetType.THIS_MONTH]: {
    createdDateFrom: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    createdDateTo: new Date()
  }
};

/**
 * Interface for filter query parameters
 */
export interface FilterQueryParams {
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  search?: string;
  projectAddressId?: string;
  mvtItemId?: string;
  vendorId?: string;
  statusCdId?: string;
  cappCategoriesId?: string;
  deliveryDateFrom?: string;
  deliveryDateTo?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  isMr?: boolean;
  mrReadYn?: string;
  hasPrice?: boolean;
  quantityMin?: number;
  quantityMax?: number;
  priceMin?: number;
  priceMax?: number;
}

/**
 * Interface for advanced search configuration
 */
export interface AdvancedSearchConfig {
  fields: SearchField[];
  operators: SearchOperator[];
  dataTypes: SearchDataType[];
}

/**
 * Interface for search field configuration
 */
export interface SearchField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'boolean';
  searchable: boolean;
  filterable: boolean;
  sortable: boolean;
  options?: { label: string; value: any }[];
}

/**
 * Interface for search operators
 */
export interface SearchOperator {
  key: string;
  label: string;
  symbol: string;
  applicableTypes: string[];
}

/**
 * Enum for search data types
 */
export enum SearchDataType {
  TEXT = 'text',
  NUMBER = 'number',
  DATE = 'date',
  BOOLEAN = 'boolean',
  SELECT = 'select'
}

/**
 * Configuration for advanced search
 */
export const ADVANCED_SEARCH_CONFIG: AdvancedSearchConfig = {
  fields: [
    { key: 'projectName', label: 'Project Name', type: 'text', searchable: true, filterable: true, sortable: true },
    { key: 'mvtItemName', label: 'Product Name', type: 'text', searchable: true, filterable: true, sortable: true },
    { key: 'vendorName', label: 'Vendor Name', type: 'text', searchable: true, filterable: true, sortable: true },
    { key: 'customerName', label: 'Customer Name', type: 'text', searchable: true, filterable: true, sortable: true },
    { key: 'statusName', label: 'Status', type: 'select', searchable: false, filterable: true, sortable: true },
    { key: 'quantity', label: 'Quantity', type: 'number', searchable: false, filterable: true, sortable: true },
    { key: 'price', label: 'Price', type: 'number', searchable: false, filterable: true, sortable: true },
    { key: 'deliveryDate', label: 'Delivery Date', type: 'date', searchable: false, filterable: true, sortable: true },
    { key: 'createdAt', label: 'Created Date', type: 'date', searchable: false, filterable: true, sortable: true },
    { key: 'isMr', label: 'Is MR', type: 'boolean', searchable: false, filterable: true, sortable: false }
  ],
  operators: [
    { key: 'equals', label: 'Equals', symbol: '=', applicableTypes: ['text', 'number', 'select', 'boolean'] },
    { key: 'contains', label: 'Contains', symbol: '∋', applicableTypes: ['text'] },
    { key: 'startsWith', label: 'Starts With', symbol: '⊲', applicableTypes: ['text'] },
    { key: 'endsWith', label: 'Ends With', symbol: '⊳', applicableTypes: ['text'] },
    { key: 'greaterThan', label: 'Greater Than', symbol: '>', applicableTypes: ['number', 'date'] },
    { key: 'lessThan', label: 'Less Than', symbol: '<', applicableTypes: ['number', 'date'] },
    { key: 'greaterThanOrEqual', label: 'Greater Than or Equal', symbol: '≥', applicableTypes: ['number', 'date'] },
    { key: 'lessThanOrEqual', label: 'Less Than or Equal', symbol: '≤', applicableTypes: ['number', 'date'] },
    { key: 'between', label: 'Between', symbol: '≬', applicableTypes: ['number', 'date'] },
    { key: 'in', label: 'In', symbol: '∈', applicableTypes: ['select'] },
    { key: 'notIn', label: 'Not In', symbol: '∉', applicableTypes: ['select'] }
  ],
  dataTypes: [
    SearchDataType.TEXT,
    SearchDataType.NUMBER,
    SearchDataType.DATE,
    SearchDataType.BOOLEAN,
    SearchDataType.SELECT
  ]
};